import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import IClientApp from "@/interfaces/IClientApp";
import ISlug from "@/interfaces/ISlug";
import capitalizeFirstLetter from "@/libs/capitalizeFirstLetter";
import dateToStandart from "@/libs/date/dateToStandart";
import transformDateFields from "@/libs/date/transformDateFields";
import {transformMultiSelect} from "@/libs/transformData/transformMultiSelect";
import transformSelect from "@/libs/transformData/transformSelect";
import Multiselect from 'vue-multiselect';
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';

import { DISPLAY_MODE_BINOM, DISPLAY_MODE_FACEBOOK, DISPLAY_MODE_APPSFLYER } from '@/../configs/displayModes';
import Loader from '@/components/Includes/Loader/Loader.vue';
import ICountry from '@/interfaces/countries/ICountry';
import ILanding from '@/interfaces/ILanding';
import IPayloadQuery from '@/interfaces/IPayloadQuery';
import ITrafficSource from '@/interfaces/ITrafficSource';
import IMultiSelectItem from '@/interfaces/selects/IMultiSelectItem';
import ICampaign from '@/interfaces/subscriptions/ICampaign';
import {Switch as cSwitch} from '@coreui/vue';

import ITracker from "@/interfaces/ITracker";
import { extractData } from '@/libs/backend/apimapper';
import urlToId from '@/libs/urlToId';
import CampaignTdsList from './CampaignTdsList/CampaignTdsList.vue';
import idToUrl from '@/libs/IdToUrl';

@Component({
    name: 'CampaignForm',
    components: {
        Loader,
        Multiselect,
        CardHeader,
        CampaignTdsList,
        cSwitch
    }
})
export default class CampaignForm extends Vue {
    @Prop(Object) private campaignData!: ICampaign;

    @Action private subscriptionsLandingsList!: (query: IPayloadQuery) => Promise<ILanding[]>;
    @Action private subscriptionsTrafficSourcesList!: (query: IPayloadQuery) => Promise<any>;
    @Action private trackersList!: () => Promise<void>;
    @Action private subscriptionsCountriesList!: (query: IPayloadQuery) => Promise<void>;

    @Getter private subscriptionsGetLandings!: () => ILanding[];
    private subscriptionsGetTrafficSources: ITrafficSource[] = [];
    @Getter private getTrackers!: () => ITracker[];
    @Getter private subscriptionsGetCountries!: () => ICountry[];

    // Begin Vuex Logic for Client Apps
    @Action private subscriptionsClientAppsList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private subscriptionsGetClientApps!: () => IClientApp[];
    // End Vuex Logic for Client Apps

    // Begin Vuex Logic for OS
    @Action private subscriptionsOsDevicesList!: (query: IPayloadQuery) => Promise<void>;
    @Getter private subscriptionsGetOsDevices!: () => ISlug[];
    // End Vuex Logic for OS
    private form: any = {};

    private loading: boolean = true;
    private readonly displayModes = [
        DISPLAY_MODE_FACEBOOK,
        DISPLAY_MODE_BINOM,
        DISPLAY_MODE_APPSFLYER
    ];

    private selectedDisplayMode = this.getSelectedDisplayMode();
    private selectedClientApps: IMultiSelectItem | null = null;
    private selectedTrafficSource: IMultiSelectItem | null = null;
    private selectedCountries: IMultiSelectItem[] = [];

    private isEdit: boolean = false;

    private get btnIcon() {
        return !this.isEdit ? 'icon-pencil' : 'icon-lock';
    }

    private get editBtnText() {
        return !this.isEdit ? 'Edit' : 'Lock';
    }

    @Watch('selectedDisplayMode', {immediate: true})
    @Watch('trafficSources')
    private changeForm() {
        const exclude:string[] = [];
        this.selectedTrafficSource = null;
        this.checkTrafficSource();

        if (this.selectedDisplayMode === DISPLAY_MODE_FACEBOOK || this.selectedDisplayMode === DISPLAY_MODE_APPSFLYER) {
            exclude.push(
                'installPostbackUrl',
                'installPostbackUrlEnabled',
                'trialPostbackUrl',
                'trialPostbackUrlEnabled',
                'subscriptionPostbackUrl',
                'subscriptionPostbackUrlEnabled',
            );
        }

        this.form = Object.entries(this.campaignData).reduce((acc: {[key: string]: string | number | boolean | null}, [key, val]) => {
            if (!exclude.includes(key)) {
                acc[key] = typeof this.form[key] === 'undefined' ? val : this.form[key];

                if (key === 'trafficSource' && this.trafficSources.length > 0) {
                    acc[key] = !acc[key] && this.trafficSources[0].value ? this.trafficSources[0].value.toString() : acc[key];
                }
            }
            return acc; 
        }, {});
    }

    private get isCreateForm() {
        this.isEdit = true;
        return this.$route.name === 'subscriptions_campaign_create';
    }

    private get btnText() {
        return this.isCreateForm ? 'Create' : 'Update';
    }

    private get trafficSourceId(): string {
        return urlToId(this.form.trafficSource);
    }

    private set trafficSourceId(value: string) {
        this.form.trafficSource = idToUrl(this.form.trafficSource, value);
    }

    private get trafficSources() {
        const getTrafficSources = this.subscriptionsGetTrafficSources.filter(ts => {
            if (this.selectedDisplayMode === DISPLAY_MODE_FACEBOOK) {
                return ts.title.toLowerCase().includes('facebook')
            } else if (this.selectedDisplayMode === DISPLAY_MODE_BINOM) {
                return !ts.title.toLowerCase().includes('facebook')
            } else {
                return ts.title.toLowerCase();
            }
        });

        return transformMultiSelect(getTrafficSources, 'title', 'id');
    }

    private get trackers() {
        const trackers = transformSelect(this.getTrackers(), 'name', 'id');

        return trackers.filter((tracker) => {
            if (this.selectedDisplayMode === DISPLAY_MODE_FACEBOOK) {
                return tracker.text.toLowerCase() === 'facebook';
            } else if (this.selectedDisplayMode === DISPLAY_MODE_APPSFLYER) {
                return tracker.text.toLowerCase() === 'appsflyer';
            } else {
                return tracker.text.toLowerCase() !== 'facebook' && tracker.text.toLowerCase() !== 'appsflyer';
            }
        });
    }

    private get clientApps() {
        return transformMultiSelect(this.subscriptionsGetClientApps(), 'title', 'id');
    }

    private get landings() {
        return transformMultiSelect(this.subscriptionsGetLandings(), 'title', 'id');
    }

    private get countries() {
        return transformMultiSelect(this.subscriptionsGetCountries());
    }

    private get countriesForTds() {
        return transformMultiSelect(this.subscriptionsGetCountries(), 'name', 'code');
    }

    private get osForTds() {
        return transformMultiSelect(this.subscriptionsGetOsDevices(), 'title', 'slug')
    }

    private editForm() {
        return this.isEdit = !this.isEdit;
    }

    private notEmpty(data: any) {
        return data.length !== 0;
    }

    private getSelectedDisplayMode() {
        const {trackerId} = this.campaignData;

        switch(trackerId) {
            case 5:
                return DISPLAY_MODE_FACEBOOK;
            case 6:
                return DISPLAY_MODE_APPSFLYER;
            default:
                return DISPLAY_MODE_BINOM;
        }
    }

    private transformValue(field: string, value: any): string {
        switch (field) {
            case 'purchaseDate':
                return dateToStandart(value);
            case 'expiresDate':
                return dateToStandart(value);
            case 'cancellationDate':
                return dateToStandart(value);
            case 'originalPurchaseDate':
                return dateToStandart(value);
            case 'subscriptionCreatedAt':
                return dateToStandart(value);
            default:
                return value ? String(value) : '–';
        }
    }

    private trackerIdChanged(event: any) :any {
        switch (event) {
            case 1:
                this.setPostbackUrlValue('http://swtchry.com/click.php?cnv_id={click_id}&payout=0.5');
                break;
            case 3:
                this.setPostbackUrlValue('https://app2.swtchry.com/click.php?cnv_id={click_id}&payout=0.5');
                break;
        }
    }

    private setPostbackUrlValue(value: any) {
        this.campaignData.postbackUrl = value;
    }

    private transformLabel(nameField: string): string {
        return capitalizeFirstLetter(nameField);
    }

    private checkDescription(nameField: string): string {
        const prefixText = 'example:';

        switch (nameField) {
            case 'title':
                return `${prefixText} Best of The Best`;
            case 'postbackUrl':
                return `${prefixText} http://swtchry.com/click.php?cnv_id={click_id}&payout=1.5`;
            default:
                return '';
        }
    }

    private isCannotEdit(field: string): boolean {
        return field === 'createdAt' || field === 'updatedAt' || field === 'id';
    }

    private disabledField(field: string): boolean {
        return !this.isCreateForm && !this.isCannotEdit(field) ? !this.isEdit && !this.isCannotEdit(field) : this.isCannotEdit(field)
    }

    private hideFields(field: string): boolean {
        return ['tds', 'id', 'installPostbackUrlEnabled', 'subscriptionPostbackUrlEnabled', 'trialPostbackUrlEnabled'].includes(field);
    }

    private sendForm(onlyCreate: boolean): void {
        const formData: any = {...this.form};

        delete formData.tds;

        if (this.selectedTrafficSource && this.selectedTrafficSource.value) {
            formData.trafficSource = `/api/subscription/subscription_traffic_sources/${this.selectedTrafficSource.value}`;
        }

        if (!formData.subscriptionPostbackUrl) {
            delete formData.subscriptionPostbackUrl;
        }

        if (!formData.installPostbackUrl) {
            delete formData.installPostbackUrl;
        }

        if (!formData.campaignId) {
            delete formData.campaignId;
        }

        if (this.selectedClientApps && this.selectedClientApps.value) {
            formData.clientAppId = this.selectedClientApps.value;
        }

        const createData = {
            createOnly: onlyCreate,
            campaignData: formData
        };

        this.isCreateForm ? this.$emit('createCampaign', createData) : this.$emit('updateCampaign', formData)
    }

    private cancelForm() {
        this.$router.go(-1);
    }

    // Begin Transform Data for Form Campaign
    private checkDateFields() {
        const dateFields: string[] = ['createdAt', 'updatedAt'];
        transformDateFields(dateFields, this.campaignData)
    }

    // End Transform Data for Form Campaign
    @Watch('clientApps', {immediate: true})
    @Watch('campaignData', {immediate: true})
    private checkClientApp() {
        if (this.clientApps) {
            this.clientApps.filter((clientApp: IMultiSelectItem) => {
                if (clientApp.value === this.campaignData.clientAppId) {
                    this.selectedClientApps = clientApp
                }
            });
        }
    }

    @Watch('trafficSources', {immediate: true})
    private checkTrafficSource() {
        if (this.trafficSources.length) {
            this.trafficSources.filter((trafficSource: IMultiSelectItem) => {
                if (trafficSource.value === +this.trafficSourceId) {
                    this.selectedTrafficSource = trafficSource;
                }
            });
        }
    }

    private async getBaseData() {
        this.loading = true;
        const dependencies = [];

        try {
            if (!this.notEmpty(this.trafficSources)) {
                dependencies.push(this.subscriptionsTrafficSourcesList({query: '?page=1&limit=300'}).then(res => this.subscriptionsGetTrafficSources = extractData(res)));
            }
            
            if (!this.notEmpty(this.trackers)) {
                dependencies.push(this.trackersList());
            }
            
            if (!this.notEmpty(this.landings)) {
                dependencies.push(this.subscriptionsLandingsList({query: '?pagination=false', metaData: true}));
            }
    
            if (!this.notEmpty(this.clientApps)) {
                dependencies.push(this.subscriptionsClientAppsList({query: '?pagination=false'}));
            }

            if (!this.notEmpty(this.osForTds)) {
                dependencies.push(this.subscriptionsOsDevicesList({query: '?pagination=false'}));
            }
    
            if (!this.notEmpty(this.countries)) {
                dependencies.push(this.subscriptionsCountriesList({query: '?pagination=false'}));
            }

            await Promise.all(dependencies);

            this.checkDateFields();
        } catch (error) {
            console.error(error);
        }

        this.loading = false;
    }

    private getCampaign() {
        this.$emit('reloadCampaign');
    }

    private async mounted() {
        try {
            await this.getBaseData();
        } catch(error) {
            console.error(error);
        }
    }
}
